import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QuarantineSettingsInput, QuarantineSettingsResponse } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export type QuarantineSettingsData = QuarantineSettingsResponse;
export type UpdateQuarantineSettingsInput = QuarantineSettingsInput;

const quarantineSettingsKey = (merchantId: string) => ["quarantine-settings", merchantId];

export const useQuarantineSettings = () => {
  const scope = useScopedMerchantClient();
  return useQuery({
    queryKey: quarantineSettingsKey(scope.merchantId),
    queryFn: async () => {
      const res = await client.merchant[":merchantId"]["quarantine-settings"].$get({
        param: { merchantId: scope.merchantId },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useUpdateQuarantineSettings = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateQuarantineSettingsInput) => {
      const res = await client.merchant[":merchantId"]["quarantine-settings"].$put({
        param: { merchantId: scope.merchantId },
        json: input,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quarantineSettingsKey(scope.merchantId) });
    },
  });
};
